/**
 * Hızlı Satış / Restoran ekran ayarlarındaki "Depo/Stok" taslakları için
 * localStorage persistence util.
 *
 * Taslaklar ŞİRKET GENELİNDEDİR — aynı şirketin tüm ekranlarından (yeni
 * açılan ekran dahil) erişilebilir. Taslağın içeriği sadece depo/stok
 * seçimleridir (depolar, kategoriler, ürünler); diğer tab ayarları
 * taslağa dahil edilmez.
 *
 * Anahtar: `quickSaleWarehouseTemplates_${companyId}`
 * Değer:   JSON.stringify(WarehouseStockTemplate[])
 */

export interface WarehouseStockTemplate {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  selectedWarehouseIds: string[];
  selectedCategoriesPerWarehouse: { [warehouseId: string]: string[] };
  selectedProductsPerCategory: { [warehouseId: string]: { [categoryId: string]: string[] } };
  // ✅ YENİ: Şube paket fiyatını kullan (StockVisibilityJson.usePackagePrices)
  // Opsiyonel: Eski taslaklar bu alana sahip değil, bu durumda undefined olarak bırakılır.
  usePackagePrices?: boolean;
}

const KEY_PREFIX = 'quickSaleWarehouseTemplates';

export function getTemplatesKey(companyId: string | null | undefined): string | null {
  if (!companyId) return null;
  return `${KEY_PREFIX}_${companyId}`;
}

function generateTemplateId(): string {
  // Tarayıcı uyumu için basit, çakışmaya dirençli id.
  return `tpl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function sanitizeTemplate(raw: any): WarehouseStockTemplate | null {
  if (!raw || typeof raw !== 'object') return null;
  const name = typeof raw.name === 'string' ? raw.name.trim() : '';
  if (!name) return null;
  const id = typeof raw.id === 'string' && raw.id ? raw.id : generateTemplateId();
  const createdAt = typeof raw.createdAt === 'number' ? raw.createdAt : Date.now();
  const updatedAt = typeof raw.updatedAt === 'number' ? raw.updatedAt : createdAt;
  const selectedWarehouseIds = Array.isArray(raw.selectedWarehouseIds)
    ? raw.selectedWarehouseIds.filter((x: unknown) => typeof x === 'string')
    : [];
  const selectedCategoriesPerWarehouse =
    raw.selectedCategoriesPerWarehouse && typeof raw.selectedCategoriesPerWarehouse === 'object'
      ? raw.selectedCategoriesPerWarehouse
      : {};
  const selectedProductsPerCategory =
    raw.selectedProductsPerCategory && typeof raw.selectedProductsPerCategory === 'object'
      ? raw.selectedProductsPerCategory
      : {};
  // ✅ YENİ: usePackagePrices opsiyonel, yoksa undefined bırak
  const usePackagePrices =
    typeof raw.usePackagePrices === 'boolean' ? raw.usePackagePrices : undefined;

  return {
    id,
    name,
    createdAt,
    updatedAt,
    selectedWarehouseIds,
    selectedCategoriesPerWarehouse,
    selectedProductsPerCategory,
    ...(usePackagePrices !== undefined ? { usePackagePrices } : {}),
  };
}

export function loadWarehouseStockTemplates(
  companyId: string | null | undefined
): WarehouseStockTemplate[] {
  const key = getTemplatesKey(companyId);
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(sanitizeTemplate)
      .filter((t): t is WarehouseStockTemplate => t !== null)
      .sort((a, b) => b.updatedAt - a.updatedAt);
  } catch (e) {
    console.warn('[warehouseStockTemplates] load parse hatası:', e);
    return [];
  }
}

function persistTemplates(
  companyId: string | null | undefined,
  templates: WarehouseStockTemplate[]
): boolean {
  const key = getTemplatesKey(companyId);
  if (!key) return false;
  try {
    localStorage.setItem(key, JSON.stringify(templates));
    return true;
  } catch (e) {
    console.warn('[warehouseStockTemplates] persist hatası:', e);
    return false;
  }
}

export interface SaveTemplateInput {
  name: string;
  selectedWarehouseIds: string[];
  selectedCategoriesPerWarehouse: { [warehouseId: string]: string[] };
  selectedProductsPerCategory: { [warehouseId: string]: { [categoryId: string]: string[] } };
  // ✅ YENİ: Şube paket fiyatını kullan (opsiyonel)
  usePackagePrices?: boolean;
}

/**
 * Yeni taslak oluşturur. Aynı isimde taslak varsa üzerine yazar (güncelleme).
 * Döner: oluşturulan/güncellenen taslak veya null.
 */
export function saveWarehouseStockTemplate(
  companyId: string | null | undefined,
  input: SaveTemplateInput
): WarehouseStockTemplate | null {
  if (!companyId) return null;
  const cleanName = (input.name || '').trim();
  if (!cleanName) return null;

  const existing = loadWarehouseStockTemplates(companyId);
  const now = Date.now();

  const duplicateIdx = existing.findIndex(
    t => t.name.trim().toLowerCase() === cleanName.toLowerCase()
  );

  let updatedTemplate: WarehouseStockTemplate;
  if (duplicateIdx >= 0) {
    updatedTemplate = {
      ...existing[duplicateIdx],
      name: cleanName,
      updatedAt: now,
      selectedWarehouseIds: input.selectedWarehouseIds,
      selectedCategoriesPerWarehouse: input.selectedCategoriesPerWarehouse,
      selectedProductsPerCategory: input.selectedProductsPerCategory,
      // ✅ YENİ: usePackagePrices sadece gönderilmişse üzerine yaz
      ...(typeof input.usePackagePrices === 'boolean'
        ? { usePackagePrices: input.usePackagePrices }
        : {}),
    };
    existing[duplicateIdx] = updatedTemplate;
  } else {
    updatedTemplate = {
      id: generateTemplateId(),
      name: cleanName,
      createdAt: now,
      updatedAt: now,
      selectedWarehouseIds: input.selectedWarehouseIds,
      selectedCategoriesPerWarehouse: input.selectedCategoriesPerWarehouse,
      selectedProductsPerCategory: input.selectedProductsPerCategory,
      // ✅ YENİ: usePackagePrices sadece gönderilmişse dahil et
      ...(typeof input.usePackagePrices === 'boolean'
        ? { usePackagePrices: input.usePackagePrices }
        : {}),
    };
    existing.unshift(updatedTemplate);
  }

  const ok = persistTemplates(companyId, existing);
  if (!ok) return null;
  console.log(`📦 [warehouseStockTemplates] kaydedildi: "${cleanName}" (id=${updatedTemplate.id})`);
  return updatedTemplate;
}

export function deleteWarehouseStockTemplate(
  companyId: string | null | undefined,
  templateId: string
): boolean {
  if (!companyId || !templateId) return false;
  const existing = loadWarehouseStockTemplates(companyId);
  const filtered = existing.filter(t => t.id !== templateId);
  if (filtered.length === existing.length) return false;
  const ok = persistTemplates(companyId, filtered);
  if (ok) {
    console.log(`📦 [warehouseStockTemplates] silindi: id=${templateId}`);
  }
  return ok;
}
